import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { translations } from '../translations';
import './AdminBlogManagement.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminBlogManagement() {
  const [posts, setPosts] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    featured_image: '',
    published: false
  });
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const language = localStorage.getItem('language') || 'en';
  const t = translations[language] || translations['en'];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      console.log('Fetching posts from:', `${BACKEND_URL}/api/admin/blog`);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setError('Not authenticated');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/blog`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Fetch response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch error:', errorText);
        throw new Error('Failed to fetch posts: ' + response.status);
      }

      const data = await response.json();
      console.log('Posts fetched:', data.posts.length);
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Failed to fetch blog posts: ' + error.message);
      setPosts([]);
    }
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      featured_image: '',
      published: false
    });
    setEditorState(EditorState.createEmpty());
    setShowEditor(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      featured_image: post.featured_image || '',
      published: post.published
    });
    
    // Convert HTML to EditorState
    try {
      const blocksFromHTML = convertFromHTML(post.content || '');
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      setEditorState(EditorState.createWithContent(contentState));
    } catch (err) {
      console.error('Error converting HTML to editor state:', err);
      setEditorState(EditorState.createEmpty());
    }
    
    setShowEditor(true);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/blog/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete post');
      }

      alert('Post deleted successfully!');
      await fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const url = editingPost
        ? `${BACKEND_URL}/api/admin/blog/${editingPost.id}`
        : `${BACKEND_URL}/api/admin/blog`;
      const method = editingPost ? 'PUT' : 'POST';

      // Convert editor state to HTML
      const contentState = editorState.getCurrentContent();
      const htmlContent = draftToHtml(convertToRaw(contentState));

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          content: htmlContent
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save post');
      }

      alert('Post saved successfully!');
      setShowEditor(false);
      setEditorState(EditorState.createEmpty());
      await fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Failed to save post: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Editor toolbar configuration
  const editorToolbar = {
    options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'image'],
    inline: {
      options: ['bold', 'italic', 'underline', 'strikethrough']
    },
    blockType: {
      options: ['Normal', 'H1', 'H2', 'H3']
    },
    list: {
      options: ['unordered', 'ordered']
    },
    textAlign: {
      options: ['left', 'center', 'right']
    }
  };

  if (error) {
    console.log('[AdminBlogManagement] Rendering error state');
    return (
      <div className="admin-blog-management">
        <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px' }}>
          <h3>Error Loading Blog Management</h3>
          <p>{error}</p>
          <button onClick={() => { setError(null); fetchPosts(); }} style={{ marginTop: '10px', padding: '8px 16px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  console.log('[AdminBlogManagement] Rendering main content, posts count:', posts.length);
  
  return (
    <div className="admin-blog-management">
      <div className="admin-blog-header">
        <h2>{t?.adminBlogManagement || 'Blog Management'}</h2>
        <button onClick={handleCreatePost} className="create-post-btn">
          + {t?.createNewPost || 'Create New Post'}
        </button>
      </div>

      {showEditor && (
        <div className="editor-modal">
          <div className="editor-overlay" onClick={() => setShowEditor(false)} />
          <div className="editor-content">
            <h3>{editingPost ? t.editPost : t.createNewPost}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t.postTitle}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder={t.postTitle}
                />
              </div>

              <div className="form-group">
                <label>{t.featuredImage}</label>
                <input
                  type="text"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="form-group">
                <label>{t.postContent}</label>
                <div style={{ border: '1px solid #ddd', minHeight: '400px', padding: '10px' }}>
                  <Editor
                    editorState={editorState}
                    onEditorStateChange={setEditorState}
                    toolbar={editorToolbar}
                    editorStyle={{
                      minHeight: '350px',
                      padding: '10px',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                  {t.publishPost}
                </label>
              </div>

              <div className="editor-actions">
                <button type="button" onClick={() => setShowEditor(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? 'Saving...' : (formData.published ? t.publishPost : t.saveDraft)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="posts-list">
        {posts.length === 0 ? (
          <p className="no-posts">{t.noBlogPosts}</p>
        ) : (
          <table className="posts-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id}>
                  <td>
                    <div className="post-title-cell">
                      {post.featured_image && (
                        <img src={post.featured_image} alt="" className="post-thumbnail" />
                      )}
                      <span>{post.title}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${post.published ? 'published' : 'draft'}`}>
                      {post.published ? t.published : t.draft}
                    </span>
                  </td>
                  <td>{new Date(post.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleEditPost(post)} className="edit-btn">
                        {t.editPost}
                      </button>
                      <button onClick={() => handleDeletePost(post.id)} className="delete-btn">
                        {t.deletePost}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminBlogManagement;
